import * as React from "react"
import type { ConsumerBillType, PaginationType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import type { DateRange } from "react-day-picker"

import { getConsumerBill } from "@/lib/api/billing"
import { DataTable } from "@/components/data-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { consumerBillColumns } from "@/components/table/columns/consumer-bill-columns"

import { Loader } from "../loader"
import { Button } from "../ui/button"
import { ExportOperationModal } from "./operations/export-operations"

type BillingTableProps = {
  status: string
  urlEndpoint: string
  date: DateRange
  setDate: React.Dispatch<React.SetStateAction<DateRange>>
  selectedRows: ConsumerBillType[]
  onSelectedRowsChange: (rows: ConsumerBillType[]) => void
  onSendReminder: () => void
}

export function BillingTable({
  status,
  urlEndpoint,
  date,
  setDate,
  selectedRows,
  onSelectedRowsChange,
  onSendReminder,
}: BillingTableProps) {
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const [defaultPage, setDefaultPage] = React.useState<number>(10)
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState<string>("")
  const [zones, setZones] = React.useState<number[]>([])
  const [books, setBooks] = React.useState<number[]>([])
  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)
  const [isSending, setIsSending] = React.useState(false) // ✅ Local state for button

  const handleSendReminder = async () => {
    if (isSending) return // Prevent multiple clicks while sending

    setIsSending(true) // Start loading
    try {
      await onSendReminder()
    } finally {
      setIsSending(false) // Stop loading
    }
  }

  const {
    data: consumerBilling,
    isLoading,
    isError,
  } = useQuery<PaginationType<ConsumerBillType>>({
    queryKey: [
      "consumerBill",
      status,
      date,
      defaultPage,
      page,
      search,
      zones,
      books,
    ],
    queryFn: async () => {
      const from = date.from ?? new Date()
      const to = date.to ?? new Date()
      console.log(moment(from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")) // Keeps local date

      const response = await getConsumerBill(
        //from.toISOString(),
        //to.toISOString(),
        moment(from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        moment(to).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        defaultPage,
        page,
        search,
        urlEndpoint,
        zones,
        books,
      )
      setNextPageUrl(response.next)
      setPrevPageUrl(response.previous)

      return response
    },
  })
  const showSelectedRows = React.useMemo(() => {
    return status !== "all"
  }, [status])

  React.useEffect(() => {
    if (consumerBilling) {
      setNextPageUrl(consumerBilling.next)
      setPrevPageUrl(consumerBilling.previous)
    }
  }, [consumerBilling])

  console.log("Table Data:", consumerBilling?.results)

  return (
    <div className="grid grid-cols-1 md:items-stretch">
      <DataTable
        columns={consumerBillColumns(status)}
        data={consumerBilling?.results ?? []}
        search={{
          label: "Bill",
          value: search,
          onChange: setSearch,
        }}
        isLoading={isLoading}
        isError={isError}
        defaultPage={defaultPage}
        setDefaultPage={setDefaultPage}
        nextPageUrl={nextPageUrl}
        prevPageUrl={prevPageUrl}
        page={page}
        setPage={setPage}
        totalCount={consumerBilling?.count}
        showSelectedRows={showSelectedRows} // ✅ Dynamic value of show selected rows label
        onSelectedRowsChange={onSelectedRowsChange} // ✅ Pass selected rows callback
        zones={zones}
        setZones={setZones}
        books={books}
        setBooks={setBooks}
      >
        <div className="flex w-auto justify-end gap-2 md:w-full">
          {status !== "all" && (
            <>
              <Button
                onClick={handleSendReminder}
                disabled={selectedRows.length === 0 || isSending}
              >
                {isSending ? (
                  <>
                    {Loader()}
                    Sending {status}...
                  </>
                ) : (
                  <>
                    Send{" "}
                    {status
                      .replace("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}{" "}
                    Reminder
                  </>
                )}
              </Button>
            </>
          )}

          {status === "all" && (
            <>
              <DateRangePicker date={date} setDate={setDate} />
            </>
          )}
          <ExportOperationModal
            tableData={consumerBilling?.results ?? [{}]}
            fileName="Billing"
            showExportModal={showExportModal}
            setShowExportModal={setShowExportModal}
          />
        </div>
      </DataTable>
    </div>
  )
}
